import { useEffect } from 'react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, MixpanelProvider, ApiProvider } from '@/contexts';
import { useAuth } from '@/api';
import { Button, LoadingIcon, ToastProvider, Typography } from '@/primer/components';
import { Maintenance } from '@/pages';
import { routeTree } from './routeTree.gen';
import { SentryErrorBoundary, ENV } from '@/config';

// Set to true to enable maintenance mode in production
const MAINTENANCE_MODE_ENABLED = true;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: {
      isAuthenticated: false,
      isLoading: true,
    },
  },
  defaultPreload: 'intent',
  defaultPendingComponent: () => (
    <div className="fixed inset-0 flex items-center justify-center">
      <LoadingIcon size="xl" />
    </div>
  ),
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const { isAuthenticated, isLoading } = useAuth();

  // Update router context with current auth state and invalidate so that
  // beforeLoad guards re-run with the settled auth state. Without invalidate(),
  // a guard that sees isLoading=true and defers will never re-evaluate after
  // auth resolves, leaving protected routes stuck on a loading spinner instead
  // of redirecting unauthenticated users to /login.
  useEffect(() => {
    router.update({
      context: {
        queryClient,
        auth: {
          isAuthenticated,
          isLoading,
        },
      },
    });
    if (!isLoading) {
      router.invalidate();
    }
  }, [isAuthenticated, isLoading]);

  return <RouterProvider router={router} />;
}

// Fallback component shown when an error is caught
const ErrorFallback = () => (
  <div className="p-8 text-center">
    <Typography variant="header" size="h1">
      Something went wrong
    </Typography>
    <Typography variant="body" size="medium">
      We've been notified and are working to fix the issue.
    </Typography>
    <Button onClick={() => window.location.reload()}>Refresh Page</Button>
  </div>
);

/**
 * Main App Component
 *
 * Provider hierarchy:
 * 1. SentryErrorBoundary - Catches all errors for monitoring
 * 2. QueryClientProvider - React Query for data fetching
 * 3. MixpanelProvider - Analytics (initialized before Auth so it's available for login/signup tracking)
 * 4. AuthProvider - Authentication state management
 * 5. ToastProvider - Primer toast notification queue and portal
 * 6. ApiProvider - Injects authenticated API client into context
 * 7. RouterProvider - TanStack Router
 *
 * Note: DevCycle is initialized in _authenticated.tsx layout to:
 * - Avoid bot traffic counting against quota
 * - Ensure user ID is available for targeting
 * - Only load feature flags for authenticated users
 */
function App() {
  // Show maintenance screen only in production when maintenance mode is enabled until app is ready to go live.
  if (MAINTENANCE_MODE_ENABLED && ENV.env === 'prod') {
    return <Maintenance />;
  }

  return (
    <SentryErrorBoundary fallback={<ErrorFallback />} showDialog>
      <QueryClientProvider client={queryClient}>
        <MixpanelProvider>
          <AuthProvider>
            <ToastProvider>
              <ApiProvider>
                <InnerApp />
              </ApiProvider>
            </ToastProvider>
          </AuthProvider>
        </MixpanelProvider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
}

export default App;
